import {
  useLayoutEffect,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

type AnchoredDialogPlacement = "anchor" | "right" | "bottom-right";

interface AnchoredDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef?: RefObject<HTMLElement | null>;
  title: string;
  children: ReactNode;
  contentClassName?: string;
  overlayClassName?: string;
  topPaddingPx?: number;
  modal?: boolean;
  closeOnOutsideInteract?: boolean;
  placement?: AnchoredDialogPlacement;
  sideOffsetPx?: number;
  widthPx?: number;
  heightPx?: number;
  offsetX?: number;
  offsetY?: number;
}

export const AnchoredDialogShell = ({
  open,
  onOpenChange,
  triggerRef,
  title,
  children,
  contentClassName = "",
  overlayClassName = "z-[11990] bg-transparent",
  topPaddingPx = 10,
  modal = true,
  closeOnOutsideInteract = true,
  placement = "anchor",
  sideOffsetPx = 24,
  widthPx = 200,
  heightPx = 310,
  offsetX = 0,
  offsetY = 0,
}: AnchoredDialogShellProps) => {
  const [position, setPosition] = useState({
    left: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
    top: 92,
    right: sideOffsetPx,
  });

  const updatePosition = () => {
    const triggerRect = triggerRef?.current?.getBoundingClientRect();

    if (placement === "bottom-right") {
      if (!triggerRect) {
        setPosition({ left: 0, top: 0, right: sideOffsetPx });
        return;
      }

      setPosition({
        left: triggerRect.right - widthPx + offsetX,
        top: triggerRect.bottom - heightPx + offsetY,
        right: sideOffsetPx,
      });
      return;
    }

    const topbarElement = document.querySelector<HTMLElement>(
      '[data-layout="topbar"]',
    );
    const computedTop = topbarElement
      ? topbarElement.getBoundingClientRect().bottom + topPaddingPx
      : triggerRect
        ? Math.max(8, triggerRect.bottom + topPaddingPx)
        : topPaddingPx;

    setPosition({
      left: triggerRect
        ? triggerRect.left + triggerRect.width / 2
        : window.innerWidth / 2,
      top: computedTop,
      right: sideOffsetPx,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [
    heightPx,
    offsetX,
    offsetY,
    open,
    placement,
    sideOffsetPx,
    topPaddingPx,
    widthPx,
  ]);

  const positionStyle =
    placement === "right"
      ? { right: `${sideOffsetPx}px`, top: `${position.top}px` }
      : { left: `${position.left}px`, top: `${position.top}px` };

  const positionClassName =
    placement === "right"
      ? "!left-auto !right-0 !translate-x-0 !translate-y-0"
      : placement === "bottom-right"
        ? "!right-auto !translate-x-0 !translate-y-0"
        : "!right-auto !-translate-x-1/2 !translate-y-0";

  const originClassName =
    placement === "bottom-right" ? "origin-bottom-right" : "origin-top";
  const animationClassName =
    placement === "bottom-right"
      ? "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
      : "data-[state=open]:slide-in-from-left-0 data-[state=closed]:slide-out-to-left-0 data-[state=open]:slide-in-from-top-6 data-[state=closed]:slide-out-to-top-6";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent
        aria-describedby={undefined}
        overlayClassName={overlayClassName}
        className={`z-[12000] ${positionClassName} ${originClassName} ${animationClassName} border-none bg-transparent p-0 shadow-none data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 [&>button]:hidden ${contentClassName}`}
        style={positionStyle}
        onInteractOutside={
          closeOnOutsideInteract
            ? undefined
            : (event) => {
                event.preventDefault();
              }
        }
        onPointerDownOutside={
          closeOnOutsideInteract
            ? undefined
            : (event) => {
                event.preventDefault();
              }
        }
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
};
